import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelFullbase,
  estimateFullbase,
  type FullbaseInputs,
  getFullbaseStatus,
  isTerminal,
  listFullbaseScans,
  pauseFullbase,
  reprocessPackages,
  resumeFullbase,
  startFullbase,
} from "@/api/fullbase";

/** Histórico de scans (jobs) — um item por varredura lógica, não por fase.
 * Re-busca quando o status muda (via invalidação do start/controles). */
export function useFullbaseScans(projectId: string | undefined) {
  return useQuery({
    queryKey: ["fullbase-scans", projectId],
    queryFn: () => listFullbaseScans(projectId as string),
    enabled: Boolean(projectId),
    staleTime: 0,
  });
}

/** Status do job. Faz polling a cada 4s enquanto não-terminal; para quando
 * completa/cancela/falha (não desperdiça request). */
export function useFullbaseStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ["fullbase-status", projectId],
    queryFn: () => getFullbaseStatus(projectId as string),
    enabled: Boolean(projectId),
    refetchInterval: (query) => {
      const s = query.state.data;
      if (!s) return 4000;
      // segue pollando enquanto não terminou (running, paused, queued...)
      return isTerminal(s) ? false : 4000;
    },
    staleTime: 0,
  });
}

export function useStartFullbase(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inputs: FullbaseInputs) =>
      startFullbase(projectId as string, inputs),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["fullbase-status", projectId] }),
  });
}

/** Reprocessa in-place os pacotes já catalogados escolhidos (reavalia com a ref
 * data atual; não cria scan novo). Invalida status + catálogo pra a UI refletir
 * a reanálise em curso e o resultado ao terminar. */
export function useReprocessPackages(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (packages: string[]) =>
      reprocessPackages(projectId as string, packages),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fullbase-status", projectId] });
      qc.invalidateQueries({ queryKey: ["wricef-by-package", projectId] });
    },
  });
}

export function useEstimateFullbase(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inputs: FullbaseInputs) =>
      estimateFullbase(projectId as string, inputs),
    // O estimate persiste uma execução própria (com cost_breakdown). A aba
    // "Effort Estimation" lê a última execução; sem invalidar aqui, ela fica
    // presa na execução do scan (sem custo) e mostra "no cost estimate".
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["executions", projectId] }),
  });
}

/** cancel/pause/resume — invalida o status na conclusão pra refletir na hora. */
export function useFullbaseControl(projectId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["fullbase-status", projectId] });
  return {
    cancel: useMutation({
      mutationFn: () => cancelFullbase(projectId as string),
      onSuccess: invalidate,
    }),
    pause: useMutation({
      mutationFn: () => pauseFullbase(projectId as string),
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: () => resumeFullbase(projectId as string),
      onSuccess: invalidate,
    }),
  };
}
